import {
  ActivityCategoryEnum,
  ActivityInstanceStatusEnum,
  ActivityScopeEnum,
  ActivityTypeEnum,
  ActivityTypeIdsEnum,
} from "@/enums/activity";
import { ActivityCardProps, ActivityClassification } from "@/types/activity";
import { IdNamePair } from "@/types/global";

export const StudentActivityCardProps: {
  [ActivityStatus: string]: ActivityCardProps;
} = Object.freeze({
  [ActivityInstanceStatusEnum.UPCOMING]: {
    ButtonName: null,
    ButtonIcon: null,
    CardColor: "tertiary",
    ActionRouteName: null,
    ShowStartEndtime: true,
  },
  [ActivityInstanceStatusEnum.PENDING]: {
    ButtonName: "Start",
    ButtonIcon: "player-play",
    CardColor: "secondary",
    ActionRouteName: "ConductActivity",
    ShowStartEndtime: true,
  },
  [ActivityInstanceStatusEnum.STARTED]: {
    ButtonName: "Resume",
    ButtonIcon: "player-play",
    CardColor: "danger",
    ActionRouteName: "ConductActivity",
    ShowStartEndtime: true,
  },
  [ActivityInstanceStatusEnum.COMPLETED]: {
    ButtonName: "Report",
    ButtonIcon: "clipboard-data",
    CardColor: "success",
    ActionRouteName: "StudentActivityReport",
    ShowStartEndtime: true,
  },
  [ActivityInstanceStatusEnum.EXPIRED]: {
    ButtonName: null,
    ButtonIcon: null,
    CardColor: "medium",
    ActionRouteName: null,
    ShowStartEndtime: true,
  },
});

// Legacy compound ActivityTypeId → new (Type, Scope, Category) triple.
// Authoritative source for migrating any hardcoded legacy classification value
// (handover §4, a bijection). Subtype is an independent axis — not decomposed here.
export const LEGACY_TYPE_DECOMPOSITION: Record<ActivityTypeIdsEnum, ActivityClassification> =
  Object.freeze({
    [ActivityTypeIdsEnum.SELF_PRACTICE]: {
      ActivityTypeId: ActivityTypeEnum.PRACTICE,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
    },
    [ActivityTypeIdsEnum.SELF_TEST]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
    },
    [ActivityTypeIdsEnum.INSTITUTE_TEST]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.INSTITUTE,
      ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
    },
    [ActivityTypeIdsEnum.ASSIGNMENT]: {
      ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
      ActivityScopeId: ActivityScopeEnum.INSTITUTE,
      ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
    },
    [ActivityTypeIdsEnum.SELF_ASSIGNMENT]: {
      ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
    },
    [ActivityTypeIdsEnum.STUDENT_PRELOADED_TEST]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
    },
    [ActivityTypeIdsEnum.INSTITUTE_PRELOADED_TEST]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.INSTITUTE,
      ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
    },
    [ActivityTypeIdsEnum.INTERNAL_PRELOADED_TEST]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.INTERNAL,
      ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
    },
    [ActivityTypeIdsEnum.PAST_YEAR_PAPER]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.PAST_YEAR,
    },
    [ActivityTypeIdsEnum.STUDENT_TEST_SERIES]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.SERIES,
    },
    [ActivityTypeIdsEnum.INSTITUTE_TEST_SERIES]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.INSTITUTE,
      ActivityCategoryId: ActivityCategoryEnum.SERIES,
    },
    [ActivityTypeIdsEnum.STUDENT_OLYMPIAD_TEST]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.OLYMPIAD,
    },
    [ActivityTypeIdsEnum.INSTITUTE_OLYMPIAD_TEST]: {
      ActivityTypeId: ActivityTypeEnum.TEST,
      ActivityScopeId: ActivityScopeEnum.INSTITUTE,
      ActivityCategoryId: ActivityCategoryEnum.OLYMPIAD,
    },
    [ActivityTypeIdsEnum.WORKBOOK_EXERCISE]: {
      ActivityTypeId: ActivityTypeEnum.WORKBOOK,
      ActivityScopeId: ActivityScopeEnum.INSTITUTE,
      ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
    },
    [ActivityTypeIdsEnum.STUDENT_PRELOADED_ASSIGNMENT]: {
      ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
    },
    [ActivityTypeIdsEnum.INSTITUTE_PRELOADED_ASSIGNMENT]: {
      ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
      ActivityScopeId: ActivityScopeEnum.INSTITUTE,
      ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
    },
    [ActivityTypeIdsEnum.INTERNAL_PRELOADED_ASSIGNMENT]: {
      ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
      ActivityScopeId: ActivityScopeEnum.INTERNAL,
      ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
    },
    [ActivityTypeIdsEnum.INTERACTIVE_VIDEO]: {
      ActivityTypeId: ActivityTypeEnum.INTERACTIVE_VIDEO,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
    },
  });

// Named activity kinds as explicit (Type, Scope, Category) combinations. This is
// the canonical identity a component/store carries for the activity it displays
// or creates — replaces the legacy compound ActivityTypeIdsEnum token.
export const ActivityKinds = {
  SelfPractice: {
    ActivityTypeId: ActivityTypeEnum.PRACTICE,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
  },
  SelfTest: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
  },
  InstituteTest: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.INSTITUTE,
    ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
  },
  Assignment: {
    ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
    ActivityScopeId: ActivityScopeEnum.INSTITUTE,
    ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
  },
  SelfAssignment: {
    ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
  },
  StudentPreloadedTest: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
  },
  InstitutePreloadedTest: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.INSTITUTE,
    ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
  },
  InternalPreloadedTest: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.INTERNAL,
    ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
  },
  PastYearPaper: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.PAST_YEAR,
  },
  StudentTestSeries: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.SERIES,
  },
  InstituteTestSeries: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.INSTITUTE,
    ActivityCategoryId: ActivityCategoryEnum.SERIES,
  },
  StudentOlympiadTest: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.OLYMPIAD,
  },
  InstituteOlympiadTest: {
    ActivityTypeId: ActivityTypeEnum.TEST,
    ActivityScopeId: ActivityScopeEnum.INSTITUTE,
    ActivityCategoryId: ActivityCategoryEnum.OLYMPIAD,
  },
  WorkbookExercise: {
    ActivityTypeId: ActivityTypeEnum.WORKBOOK,
    ActivityScopeId: ActivityScopeEnum.INSTITUTE,
    ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
  },
  StudentPreloadedAssignment: {
    ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
  },
  InstitutePreloadedAssignment: {
    ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
    ActivityScopeId: ActivityScopeEnum.INSTITUTE,
    ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
  },
  InternalPreloadedAssignment: {
    ActivityTypeId: ActivityTypeEnum.ASSIGNMENT,
    ActivityScopeId: ActivityScopeEnum.INTERNAL,
    ActivityCategoryId: ActivityCategoryEnum.PREDESIGNED,
  },
  InteractiveVideo: {
    ActivityTypeId: ActivityTypeEnum.INTERACTIVE_VIDEO,
    ActivityScopeId: ActivityScopeEnum.STUDENT,
    ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
  },
} satisfies Record<string, ActivityClassification>;

// Stable string key for an activity classification — used to look up per-kind
// config (ActivityTypeConfigs) by the (Type, Scope, Category) combination.
export function activityKindKey(classification: {
  ActivityTypeId: number;
  ActivityScopeId: number;
  ActivityCategoryId: number;
}): string {
  return `${classification.ActivityTypeId}-${classification.ActivityScopeId}-${classification.ActivityCategoryId}`;
}

// Activity types offered in the predesigned-admin "Activity Type" selector.
export const PredesignedActivityTypeOptions: Array<IdNamePair> = [
  { Id: ActivityTypeEnum.ASSIGNMENT, Name: "Assignment" },
  { Id: ActivityTypeEnum.TEST, Name: "Test" },
];
